import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardBody, CardHeader, CardTitle, Spinner } from "reactstrap";
import {
  IoBanOutline,
  IoCashOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoCreateOutline,
  IoDocumentTextOutline,
  IoLogInOutline,
  IoPersonAddOutline,
  IoTimeOutline,
  IoTrashOutline
} from "react-icons/io5";
import { get } from "../../../../../../utils/helpers/api";
import { useSettings } from "../../../../../../context/settings";
import { printLeaveType } from "../../../../utils/helpers";
import { documentTypeLabel } from "../../../../utils/documents";
import { printSlug } from "../../../../../../utils/helpers/text";

const PAGE_SIZE = 25;

const FILTERS = ['profile', 'leave', 'document', 'payroll', 'attendance'];

/**
 * The backend sends structured events rather than sentences, so the wording,
 * the icon and the accent colour are all decided here from the type and action.
 */
function describeEvent(event, { t, formatDate, formatTime }) {
  const meta = event.meta || {};

  const leaveLabel = () => printLeaveType(meta.leave_type) || t('hr.leaveRequest');
  const leaveRange = () => {
    const start = formatDate(meta.start_date);
    const end = formatDate(meta.end_date);
    const days = meta.days;
    return start === end
      ? t('hr.activityEvent.leaveSingleDay', { days, start })
      : t('hr.activityEvent.leaveRange', { days, start, end });
  };

  switch (`${event.type}.${event.action}`) {
    case 'profile.created':
      return {
        color: '#6C63FF',
        icon: <IoPersonAddOutline size={13} />,
        title: t('hr.activityEvent.profileCreated')
      };
    case 'profile.updated': {
      const fields = meta.fields || [];
      return {
        color: '#6C63FF',
        icon: <IoCreateOutline size={13} />,
        title: t('hr.activityEvent.profileUpdated'),
        description: fields.length
          ? `${t('hr.activityEvent.fieldsChanged', { count: fields.length })} · ${fields.slice(0, 4).map(printSlug).join(', ')}${fields.length > 4 ? '…' : ''}`
          : null
      };
    }
    case 'profile.deleted':
      return {
        color: '#ef4444',
        icon: <IoTrashOutline size={13} />,
        title: t('hr.activityEvent.profileDeleted')
      };

    case 'leave.requested':
      return {
        color: '#0ea5e9',
        icon: <IoTimeOutline size={13} />,
        title: t('hr.activityEvent.leaveRequested', { type: leaveLabel() }),
        description: leaveRange()
      };
    case 'leave.approved':
      return {
        color: '#22c55e',
        icon: <IoCheckmarkCircleOutline size={13} />,
        title: t('hr.activityEvent.leaveApproved', { type: leaveLabel() }),
        description: leaveRange()
      };
    case 'leave.rejected':
      return {
        color: '#ef4444',
        icon: <IoCloseCircleOutline size={13} />,
        title: t('hr.activityEvent.leaveRejected', { type: leaveLabel() }),
        description: leaveRange()
      };
    case 'leave.cancelled':
      return {
        color: '#98a2b3',
        icon: <IoBanOutline size={13} />,
        title: t('hr.activityEvent.leaveCancelled', { type: leaveLabel() }),
        description: leaveRange()
      };

    case 'document.issued':
      return {
        color: '#8b5cf6',
        icon: <IoDocumentTextOutline size={13} />,
        title: t('hr.activityEvent.documentIssued', { type: documentTypeLabel(meta.document_type) }),
        description: meta.reference
      };
    case 'document.cancelled':
      return {
        color: '#98a2b3',
        icon: <IoBanOutline size={13} />,
        title: t('hr.activityEvent.documentCancelled', { type: documentTypeLabel(meta.document_type) }),
        description: meta.reference
      };

    case 'payroll.payslip':
      return {
        color: '#f59e0b',
        icon: <IoCashOutline size={13} />,
        title: t('hr.activityEvent.payslipGenerated'),
        description: [
          meta.reference,
          meta.period_start && t('hr.activityEvent.payslipPeriod', {
            start: formatDate(meta.period_start),
            end: formatDate(meta.period_end)
          })
        ].filter(Boolean).join(' · ')
      };

    case 'attendance.absent':
      return {
        color: '#ef4444',
        icon: <IoCloseCircleOutline size={13} />,
        title: t('hr.activityEvent.attendanceAbsent')
      };
    case 'attendance.leave':
      return {
        color: '#0ea5e9',
        icon: <IoTimeOutline size={13} />,
        title: t('hr.activityEvent.attendanceLeave')
      };
    case 'attendance.present':
    default:
      return {
        color: '#22c55e',
        icon: <IoLogInOutline size={13} />,
        title: meta.total_hours
          ? t('hr.activityEvent.attendanceHours', { hours: meta.total_hours })
          : t('hr.activityEvent.attendancePresent'),
        description: meta.check_in_time
          ? (meta.check_out_time
              ? t('hr.activityEvent.attendanceCheckedRange', {
                  in: formatTime(meta.check_in_time),
                  out: formatTime(meta.check_out_time)
                })
              : t('hr.activityEvent.attendanceCheckedIn', { time: formatTime(meta.check_in_time) }))
          : null
      };
  }
}

function ActivitiesTab({ employeeId }) {
  const { t } = useTranslation();
  const { formatDate, formatTime, formatDateTime } = useSettings();
  const [events, setEvents] = useState([]);
  const [activeTypes, setActiveTypes] = useState([]);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!employeeId) return;

    let cancelled = false;

    const getActivities = async () => {
      setLoading(true);

      const params = new URLSearchParams({ limit });
      activeTypes.forEach(type => params.append('types[]', type));

      const data = await get(`/hr/employees/${employeeId}/activities?${params}`);

      if (cancelled) return;

      if (data?.success) {
        setEvents(data.events);
        setHasMore(data.has_more);
        setError(false);
      } else {
        setError(true);
      }

      setLoading(false);
    };

    getActivities();

    return () => { cancelled = true; };
  }, [employeeId, limit, activeTypes]);

  const toggleType = (type) => {
    setLimit(PAGE_SIZE);
    setActiveTypes(prev =>
      prev.includes(type) ? prev.filter(item => item !== type) : [...prev, type]
    );
  };

  return (
    <Card style={{ maxWidth: 760 }}>
      <CardHeader>
        <CardTitle>
          <IoTimeOutline size={18} /> {t('hr.recentActivity')}
        </CardTitle>
      </CardHeader>
      <CardBody>
        <div className="emp-chips">
          <button
            type="button"
            className={"emp-chip" + (activeTypes.length === 0 ? " is-active" : "")}
            onClick={() => { setLimit(PAGE_SIZE); setActiveTypes([]); }}
          >
            {t('hr.allActivity')}
          </button>
          {FILTERS.map(type => (
            <button
              key={type}
              type="button"
              className={"emp-chip" + (activeTypes.includes(type) ? " is-active" : "")}
              onClick={() => toggleType(type)}
            >
              {t(`hr.activityType.${type}`)}
            </button>
          ))}
        </div>

        {loading && events.length === 0 ?
          <Spinner color="primary" className="d-block mx-auto my-4" />
        : error ?
          <div className="emp-empty">{t('hr.failedLoadActivity')}</div>
        : events.length === 0 ?
          <div className="emp-empty">{t('hr.noActivity')}</div>
        :
          <>
            <div className="emp-timeline">
              {events.map(event => {
                const { color, icon, title, description } = describeEvent(event, { t, formatDate, formatTime });
                const stamp = event.precision === 'date'
                  ? formatDate(event.date)
                  : formatDateTime(event.date);

                return (
                  <div className="emp-tl-item" key={event.id}>
                    <span className="emp-tl-dot" style={{ background: color }}>{icon}</span>
                    <div className="emp-tl-title">{title}</div>
                    {description && <div className="emp-tl-desc">{description}</div>}
                    <div className="emp-tl-time">
                      {stamp}
                      {event.actor && ` · ${t('hr.activityEvent.by', { name: event.actor })}`}
                    </div>
                  </div>
                );
              })}
            </div>

            {hasMore &&
              <div className="text-center pt-2">
                <button
                  type="button"
                  className="emp-btn"
                  disabled={loading}
                  onClick={() => setLimit(prev => prev + PAGE_SIZE)}
                >
                  {loading ? t('common.loading') : t('hr.loadMore')}
                </button>
              </div>
            }
          </>
        }
      </CardBody>
    </Card>
  );
}

export default ActivitiesTab;
